import type { EditorValue } from 'components/RichText/types';
import type { EmbeddedUser } from 'modules/core/types';

/** The topic metadata shared by every new comments transport. */
export type TopicSnapshot = {
  version: number;
  count: number;
  first_id: number | null;
  last_id: number | null;
};

/** A topic summary embedded in a parent object. */
export type EmbeddedTopic = TopicSnapshot & {
  object_key: string;
};

/** What survives a deletion: the positional facts, and nothing else. */
type CommentBase = {
  id: number;
  created_at: string;
};

export type LiveComment = CommentBase & {
  deleted: false;
  content: EditorValue;
  created_by: EmbeddedUser;
};

/**
 * A deleted comment keeps its id and position, but the server withholds the
 * body and the author, so both are null on every transport. Narrowing on
 * `deleted` is what forces a view to stop reading them.
 */
export type TombstoneComment = CommentBase & {
  deleted: true;
  content: null;
  created_by: null;
};

export type Comment = LiveComment | TombstoneComment;

export type PageResponse = TopicSnapshot & {
  items: Comment[];
};

/**
 * A `limit=0` refresh. Deliberately not a `PageResponse`: it carries an empty
 * `items` on the wire, and dispatching that as a `PAGE` would discard the very
 * window the summary-only request exists to preserve.
 */
export type SummaryResponse = TopicSnapshot;

export type CreateResponse = TopicSnapshot & {
  comment: Comment;
  prev_id: number | null;
};

export type DeleteResponse = TopicSnapshot & {
  id: number;
};

export type Edge = 'initial' | 'older' | 'newer';

export type CommentState = {
  version: number;
  count: number;
  firstId: number | null;
  lastId: number | null;
  /** Sorted by id. Tombstones retain their original position. */
  items: Comment[];
  loadedFrom: number | null;
  loadedTo: number | null;
  loading: Partial<Record<Edge, true>>;
  errors: Partial<Record<Edge, Error>>;
  /** The comment the window was built around, and the card the view highlights. */
  anchor: number | null;
  /** A mounted stream. Only an open topic may issue background requests. */
  open: boolean;
  /** Creates that landed while closed. Not "unread": nobody tracks which ids were seen. */
  updates: number;
};

export type CommentAction =
  | { type: 'SEED'; topic: TopicSnapshot }
  | { type: 'PAGE'; edge: Edge; response: PageResponse }
  | { type: 'CREATED'; comment: Comment; prevId: number | null; topic: TopicSnapshot }
  | { type: 'DELETED'; id: number; topic: TopicSnapshot }
  | { type: 'REQUEST'; edge: Edge }
  | { type: 'FAIL'; edge: Edge; error: Error }
  | { type: 'OPEN'; anchor: number | null }
  | { type: 'CLOSE' };

/**
 * Use a factory when a reducer needs a fresh state. Version -1 deliberately
 * accepts the version-zero snapshot of both persisted and synthetic topics.
 */
export function createCommentState(): CommentState {
  return {
    version: -1,
    count: 0,
    firstId: null,
    lastId: null,
    items: [],
    loadedFrom: null,
    loadedTo: null,
    loading: {},
    errors: {},
    anchor: null,
    open: false,
    updates: 0,
  };
}

function mergeSnapshot(state: CommentState, topic: TopicSnapshot): CommentState {
  if (topic.version <= state.version) {
    return state;
  }

  return {
    ...state,
    version: topic.version,
    count: topic.count,
    firstId: topic.first_id,
    lastId: topic.last_id,
  };
}

function withoutEdge<T>(values: Partial<Record<Edge, T>>, edge: Edge): Partial<Record<Edge, T>> {
  const { [edge]: _removed, ...rest } = values;
  return rest;
}

function mergeItems(...lists: Comment[][]): Comment[] {
  const byId = new Map<number, Comment>();
  for (const list of lists) {
    for (const item of list) {
      const existing = byId.get(item.id);
      // Deletion is terminal, so an older page may not resurrect a tombstone
      // received while that page was in flight.
      if (!existing || !existing.deleted || item.deleted) {
        byId.set(item.id, item);
      }
    }
  }
  return [...byId.values()].sort((left, right) => left.id - right.id);
}

function tombstone({ id, created_at }: Comment): TombstoneComment {
  return { id, created_at, deleted: true, content: null, created_by: null };
}

function bounds(items: Comment[]): Pick<CommentState, 'loadedFrom' | 'loadedTo'> {
  if (items.length === 0) {
    return { loadedFrom: null, loadedTo: null };
  }

  return {
    loadedFrom: items[0].id,
    loadedTo: items[items.length - 1].id,
  };
}

function clearRequest(state: CommentState, edge: Edge): CommentState {
  return {
    ...state,
    loading: withoutEdge(state.loading, edge),
    errors: withoutEdge(state.errors, edge),
  };
}

/**
 * Drop the window and every request that belonged to it, keeping the topic
 * summary. Both lifecycle transitions discard a window; only they release the
 * per-edge request slots wholesale.
 */
function clearWindow(state: CommentState): CommentState {
  return {
    ...state,
    items: [],
    loadedFrom: null,
    loadedTo: null,
    loading: {},
    errors: {},
  };
}

export function commentReducer(state: CommentState, action: CommentAction): CommentState {
  switch (action.type) {
    case 'SEED':
      return mergeSnapshot(state, action.topic);

    case 'REQUEST':
      return {
        ...state,
        loading: { ...state.loading, [action.edge]: true },
        errors: withoutEdge(state.errors, action.edge),
      };

    case 'FAIL':
      return {
        ...state,
        loading: withoutEdge(state.loading, action.edge),
        errors: { ...state.errors, [action.edge]: action.error },
      };

    case 'OPEN':
      // A rebuild re-enters here, so `updates` resets on every window build.
      // That is a no-op except on the transition out of closed, which is the
      // only state in which the counter can grow.
      return { ...clearWindow(state), anchor: action.anchor, open: true, updates: 0 };

    case 'CLOSE':
      // `anchor` survives; the next OPEN overwrites it.
      return { ...clearWindow(state), open: false };

    case 'PAGE': {
      const withSnapshot = mergeSnapshot(state, action.response);
      const withRequestCleared = clearRequest(withSnapshot, action.edge);
      const items =
        action.edge === 'initial'
          ? mergeItems(action.response.items)
          : mergeItems(withRequestCleared.items, action.response.items);

      return {
        ...withRequestCleared,
        items,
        ...bounds(items),
      };
    }

    case 'CREATED': {
      const withSnapshot = mergeSnapshot(state, action.topic);
      // Identity means the version gate rejected this create: it is our own POST
      // echo or a replay. Neither is news.
      if (withSnapshot === state) {
        return state;
      }
      // A closed topic has no window to place into, so the only thing worth
      // recording is that something arrived.
      if (!state.open) {
        return { ...withSnapshot, updates: withSnapshot.updates + 1 };
      }
      // An initial page replaces its window, so placement is suspended until it lands.
      if (state.loading.initial || action.prevId !== state.loadedTo) {
        return withSnapshot;
      }

      const items = mergeItems(withSnapshot.items, [action.comment]);
      return {
        ...withSnapshot,
        items,
        ...bounds(items),
      };
    }

    case 'DELETED': {
      const withSnapshot = mergeSnapshot(state, action.topic);
      const index = withSnapshot.items.findIndex((item) => item.id === action.id);
      if (index === -1 || withSnapshot.items[index].deleted) {
        return withSnapshot;
      }

      const items = [...withSnapshot.items];
      items[index] = tombstone(items[index]);
      return { ...withSnapshot, items };
    }
  }
}

export function hasOlder(state: CommentState): boolean {
  return state.loadedFrom !== null && state.firstId !== null && state.loadedFrom > state.firstId;
}

export function atEnd(state: CommentState): boolean {
  return state.lastId === null || (state.loadedTo !== null && state.loadedTo >= state.lastId);
}

export function hasNewer(state: CommentState): boolean {
  return !atEnd(state);
}
